import * as React from "react";
import { useCompactPane } from "../../../hooks/useCompactPane";
import { CloudUploadIcon, LockOpenIcon } from "../../../components/icons";
import { Button } from "../../../components/ui/button";
import { Tooltip } from "../../../components/ui/tooltip";
import { cn } from "../../../lib/utils";
import { DocumentCheckOutAdapter } from "../../../platform";
import { useCheckOutVerification, verificationMessage } from "../hooks/useCheckOutVerification";
import CheckOutOverlay from "./CheckOutOverlay";
import { CheckOutAction } from "../types/check-out-action";

// Save and Check in, in the pane's header rather than on the ribbon.
//
// They were ribbon buttons, and moving them fixes two things that could not be fixed
// while they stayed there.
//
// The first is that the ribbon could not read the document. Pressing a ribbon button
// while a cell is being edited fires the handler but does NOT leave edit mode, and the
// Office APIs refuse every call until it does -- so Save straight after typing failed,
// and kept failing however long you waited. Clicking anywhere in the pane commits the
// edit. A button in the pane is a click in the pane, so by the time the handler runs
// the edit is committed and the APIs answer. The problem cannot occur here.
//
// The second is that the ribbon could not tell the truth about its own state. The
// shared runtime does not start until the add-in is first engaged, so on a first open
// -- which is every freshly checked-out document -- no code of ours had run and any
// ribbon state would have been a guess; the buttons therefore shipped permanently
// enabled and the pane explained afterwards. Here we are the thing that just loaded,
// so by the time these render the answer is known and a disabled button can say why.

interface CheckOutRequest {
  action: CheckOutAction;
  /** Bumped per press so a repeat press remounts the surface and runs again. */
  id: number;
}

export interface CheckOutActionsProps {
  /** Passed in rather than read from context: a host without one never renders this. */
  documents: DocumentCheckOutAdapter;
}

const CheckOutActions = ({ documents }: CheckOutActionsProps) => {
  const verification = useCheckOutVerification(documents);
  const [request, setRequest] = React.useState<CheckOutRequest | null>(null);
  const closeRequest = React.useCallback(() => setRequest(null), []);

  const press = (action: CheckOutAction) =>
    setRequest((previous) => ({ action, id: (previous?.id ?? 0) + 1 }));

  // The precise status, once per change of answer. The tooltips say the same thing in
  // prose, but they say it only to someone hovering the right button -- this is here so
  // a report of "the buttons are wrong" can be answered without a rebuild.
  React.useEffect(() => {
    console.log(`Check out actions: ${verification.status}`);
  }, [verification.status]);

  const saveUnavailable = documents.describeSaveUnavailable();

  // Every state renders both buttons; only the reason changes. Earlier this returned
  // null for "no-identity" and "loading", which was defensible on its own terms -- a
  // header the user asked no question of should not answer one -- but it meant the two
  // states that are hardest to tell apart were also the two that showed nothing at all,
  // and a missing button cannot explain its own absence. A disabled button with the
  // reason on it can, and it costs a document that was never checked out nothing but a
  // pair of greyed controls.
  const blocked =
    verification.status === "loading"
      ? "Checking whether this document is checked out from Jylo…"
      : (verification.readFailure ??
        (verification.status !== "ok"
          ? verificationMessage(verification.status, verification.state)
          : null));

  // A document that is not in Jylo at all is not blocked from saving: Save asks where it
  // should go, uploads it and checks it out, and the two buttons then work as they do on
  // any other check out. Check in stays disabled until then -- there is nothing to publish.
  const notInJylo = verification.status === "no-identity";

  const saveReason = notInJylo ? saveUnavailable : (blocked ?? saveUnavailable);
  const checkInReason = notInJylo
    ? "This document is not in Jylo yet. Save it to Jylo first, then check it in when you are done."
    : blocked;

  // A failed read is the one blocked state that does NOT disable, because it is the one
  // we might be wrong about: the host would not answer, which is not the same as an
  // answer. It used to grey both buttons out, and the pane's only retry -- "Try again"
  // in SaveView -- sits behind the Save button, so the failure disabled the control
  // that recovers from it. In Excel that is not hypothetical: the host refuses the
  // Rich API while a cell is mid-edit, and the way out is to click into the pane and
  // press Save, which was exactly what could not be done.
  const recoverable = verification.status === "read-failed";

  // The pane IS the viewport, so a media query measures the pane. Below this width
  // the labels go and the icons carry the buttons, with the label moving into the
  // tooltip so it is still there for anyone who hovers.
  const compact = useCompactPane();

  // Enabled reads as text.primary, not text.secondary. These are the two things the
  // header lets you do, and when they were secondary the disabled state had nowhere
  // left to go: a quiet grey stepping down to a slightly quieter grey, on a text
  // button with no fill or border to change shape. Worst in dark, where both greys
  // sit mid-scale. Disabled is named rather than left to the Button's stock 50% fade
  // so the two states are a decision here and can be read off against each other.
  const buttonColour = (disabled: boolean) =>
    cn(
      disabled ? "text-disabled" : "text-foreground",
      "disabled:text-disabled disabled:opacity-100"
    );

  const action = (
    label: string,
    Icon: React.ComponentType<{ className?: string }>,
    reason: string | null,
    fallbackTip: string,
    onClick: () => void
  ) => {
    const disabled = reason !== null && !recoverable;
    return (
      <Tooltip title={reason ?? (compact ? `${label}: ${fallbackTip}` : fallbackTip)}>
        {/* A disabled button fires no events, so the tooltip needs a wrapper to hang
          off -- otherwise the reason it is disabled is exactly what you cannot read. */}
        <span className="inline-flex">
          {compact ? (
            <Button
              variant="ghost"
              size="icon"
              aria-label={label}
              disabled={disabled}
              onClick={onClick}
              className={buttonColour(disabled)}
            >
              <Icon className="size-5" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              disabled={disabled}
              onClick={onClick}
              className={cn(buttonColour(disabled), "px-2")}
            >
              <Icon className="size-[18px]" />
              {label}
            </Button>
          )}
        </span>
      </Tooltip>
    );
  };

  return (
    <>
      <div className="flex flex-row gap-0.5">
        {action(
          "Save",
          CloudUploadIcon,
          saveReason,
          notInJylo
            ? "Save this document into a Jylo project"
            : "Save your changes to Jylo without creating a version",
          () => press(notInJylo ? "saveNew" : "save")
        )}
        {action(
          "Check in",
          LockOpenIcon,
          checkInReason,
          "Publish a new version and end this check out",
          () => press("checkin")
        )}
      </div>

      <CheckOutOverlay
        documents={documents}
        action={request?.action ?? null}
        requestId={request?.id ?? 0}
        onClose={closeRequest}
        onLinked={verification.refresh}
      />
    </>
  );
};

export default CheckOutActions;
